/**
 * Client-side filters for the members list (/members/).
 */

export const MEMBER_SEARCH_FIELDS = [
  'AccountNumber',
  'FullName',
  'SurName',
  'OtherName',
  'RegisteredPhoneNumber1',
  'RegisteredPhoneNumber2',
  'EmailAddress',
  'IDNumber',
  'created_by',
  'membertypename',
  'AreaOfResidence',
  'District',
];

export const ACCOUNT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'halt', label: 'Halt' },
];

export function memberHasLoan(member) {
  return Number(member.loan_balance || 0) > 0 || Number(member.loaninterest_balance || 0) > 0;
}

export function memberHasSavings(member) {
  return Number(member.savings_balance || 0) > 0;
}

export function memberHasZeroBalances(member) {
  const fields = [
    'savings_balance',
    'sharesamount_balance',
    'sharesnumber_balance',
    'loan_balance',
    'loaninterest_balance',
    'loanfine_balance',
    'fixed_deposit_balance',
  ];
  return fields.every((field) => Math.abs(Number(member[field] || 0)) < 0.01);
}

export function filterMembers(allMembers, filters) {
  const {
    searchText = '',
    gender = '',
    memberTypeId = '',
    accountStatus = '',
    accountKind = 'all',
    hasLoan = 'all',
    hasSavings = 'all',
    zeroBalancesOnly = false,
  } = filters;

  let rows = Array.isArray(allMembers) ? allMembers.slice() : [];

  const search = String(searchText || '').trim().toLowerCase();
  if (search) {
    rows = rows.filter((member) => {
      const haystack = MEMBER_SEARCH_FIELDS.map((field) => member[field])
        .filter((value) => value !== undefined && value !== null && value !== '')
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }

  if (gender) {
    rows = rows.filter(
      (member) => String(member.gender || '').toLowerCase() === String(gender).toLowerCase()
    );
  }

  if (memberTypeId) {
    rows = rows.filter((member) => String(member.memberType) === String(memberTypeId));
  }

  if (accountStatus) {
    rows = rows.filter(
      (member) => String(member.account_status || '').toLowerCase() === accountStatus.toLowerCase()
    );
  }

  if (accountKind === 'individual') {
    rows = rows.filter((member) => member.groupAccount !== true);
  } else if (accountKind === 'group') {
    rows = rows.filter((member) => member.groupAccount === true);
  }

  if (hasLoan === 'yes') {
    rows = rows.filter((member) => memberHasLoan(member));
  } else if (hasLoan === 'no') {
    rows = rows.filter((member) => !memberHasLoan(member));
  }

  if (hasSavings === 'yes') {
    rows = rows.filter((member) => memberHasSavings(member));
  } else if (hasSavings === 'no') {
    rows = rows.filter((member) => !memberHasSavings(member));
  }

  if (zeroBalancesOnly) {
    rows = rows.filter((member) => memberHasZeroBalances(member));
  }

  return rows;
}

export function buildMembersApiQuery(saccoId, filters) {
  const params = new URLSearchParams();
  if (saccoId) {
    params.set('sacco', saccoId);
  }
  const search = String(filters.searchText || '').trim();
  if (search) {
    params.set('search', search);
  }
  if (filters.gender) {
    params.set('gender', filters.gender);
  }
  if (filters.memberTypeId) {
    params.set('member_type', filters.memberTypeId);
  }
  if (filters.accountStatus) {
    params.set('account_status', filters.accountStatus);
  }
  if (filters.accountKind === 'group') {
    params.set('groupAccount', 'true');
  } else if (filters.accountKind === 'individual') {
    params.set('groupAccount', 'false');
  }
  if (filters.hasLoan === 'yes') {
    params.set('has_loan', 'true');
  } else if (filters.hasLoan === 'no') {
    params.set('has_loan', 'false');
  }
  if (filters.hasSavings === 'yes') {
    params.set('has_savings', 'true');
  } else if (filters.hasSavings === 'no') {
    params.set('has_savings', 'false');
  }
  if (filters.zeroBalancesOnly) {
    params.set('zero_balances', 'true');
  }
  return params.toString();
}

export function hasActiveMemberFilters(filters) {
  return Boolean(
    (filters.searchText && filters.searchText.trim())
    || filters.gender
    || filters.memberTypeId
    || filters.accountStatus
    || (filters.accountKind && filters.accountKind !== 'all')
    || (filters.hasLoan && filters.hasLoan !== 'all')
    || (filters.hasSavings && filters.hasSavings !== 'all')
    || filters.zeroBalancesOnly
  );
}
